import React, {ChangeEvent, memo, useEffect, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {useScript} from "../hooks/useScript";
import {useUrlParamExists} from "../hooks/useUrlParamExists";
import {isAndroidWebView, isWebView} from "../isWebView";
import {TipCurrency, maxTipAmount} from "../model/constants";
import {getTrackingTip, ShareKeyType, TrackingTip} from "../model/paymentApi";
import {getSearchParam} from "../utils/getUrlParam";
import {ApplePay} from "./ApplePay";
import {GooglePay} from "./GooglePay";
import {PayByCard} from "./PayByCard";
import {PaymentOutcome} from "./PaymentOutcome";
import "../styles/Tips.css";
import {TipsReceipt} from "./TipsReceipt";

declare global {
    interface Window {
        ApplePaySession: any;
    }
}

interface TipsProps {
    tipAmounts: number[];
    currency: TipCurrency;
    shareKey: string;
    shareKeyType: ShareKeyType;
}

export const Tips = memo(function Tips({tipAmounts, currency, shareKey, shareKeyType}: TipsProps) {
    const [selectedAmount, setSelectedAmount] = useState<string | undefined>(String(tipAmounts[0]));
    const googlePayLoaded = useScript("https://pay.google.com/gp/p/js/pay.js");
    const applePayLoaded = useScript("https://applepay.cdn-apple.com/jsapi/v1/apple-pay-sdk.js");
    const [showGooglePay, setShowGooglePay] = useState(true);
    const [showApplePay, setShowApplePay] = useState(true);
    const [customSelected, setCustomSelected] = useState(false);
    const [customAmount, setCustomAmount] = useState("");
    const [customTouched, setCustomTouched] = useState(false);
    const [tips, setTips] = useState<TrackingTip[] | null>(null);
    const mobileAppSource = useUrlParamExists("utm_source", "mobileApp");
    const outcome = getSearchParam("outcome");
    const [addMore, setAddMore] = useState(false);
    const customInputRef = useRef<HTMLInputElement>(null);

    const {formatNumber} = useIntl();
    const MAX_PRICE = maxTipAmount[currency];
    const isCustomInvalid =
        customSelected &&
        customTouched &&
        (Number(customAmount) < 1 || Number(customAmount) > MAX_PRICE);

    useEffect(() => {
        getTrackingTip(shareKeyType, shareKey).then((response) => setTips(response));
    }, [shareKey, shareKeyType]);

    if (!tips) {
        return null;
    }

    function paymentType(price: string) {
        if (window.ApplePaySession && window.ApplePaySession?.canMakePayments && showApplePay) {
            return (
                <>
                    <ApplePay
                        price={price}
                        currency={currency}
                        setShowApplePay={setShowApplePay}
                        shareKey={shareKey}
                        shareKeyType={shareKeyType}
                    />
                    {!isWebView() && (
                        <PayByCard
                            price={price}
                            currency={currency}
                            kind="secondary"
                            shareKey={shareKey}
                            shareKeyType={shareKeyType}
                        />
                    )}
                </>
            );
        }
        if (showGooglePay) {
            return (
                <>
                    {!isWebView() && (
                        //Won't show in webview as UX is bad. Android will open in custom tabs (chrome) after the ride is finished, in oppose to iOS Android will not have option to tip during the ride
                        <GooglePay
                            price={price}
                            currency={currency}
                            setShowGooglePay={setShowGooglePay}
                            shareKey={shareKey}
                            shareKeyType={shareKeyType}
                        />
                    )}
                    {!isWebView() && !mobileAppSource && (
                        <PayByCard
                            price={price}
                            currency={currency}
                            kind="secondary"
                            shareKey={shareKey}
                            shareKeyType={shareKeyType}
                        />
                    )}
                </>
            );
        }
        if (!isWebView()) {
            return (
                <PayByCard
                    price={price}
                    currency={currency}
                    kind="primary"
                    shareKey={shareKey}
                    shareKeyType={shareKeyType}
                />
            );
        }
    }

    function handleChangeTip(event: any) {
        setSelectedAmount(event.target.value);
        setCustomSelected(false);
        setCustomAmount("");
        setCustomTouched(false);
    }

    const handleCustomAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^0-9,.]/g, "");
        value = value.replace(",", ".");
        setCustomTouched(true);
        setCustomAmount(value);
    };

    const handleSelectCustom = () => {
        setSelectedAmount(undefined);
        setCustomSelected(true);
        setCustomTouched(false);
        setTimeout(() => customInputRef.current?.focus(), 0);
    };

    const handleBlur = () => {
        if (!customAmount) {
            setCustomSelected(false);
            setCustomTouched(false);
        }
    };

    //FIXME: Need to sync showing pay buttons with tip amounts (so never show amount without any pay button)
    if (isAndroidWebView()) {
        return null;
    }
    return (
        <>
            {outcome && <PaymentOutcome outcome={outcome} />}
            {!tips || tips.length === 0 || addMore ? (
                <div className="tip-header">
                    <h3>
                        <FormattedMessage id="tip_for_courier" />
                    </h3>
                    <small className="tip-info">
                        <FormattedMessage id="tip_for_courier_description" />
                    </small>
                    <div className="switch-group" data-cy="tracking-tips-panel">
                        {tipAmounts.map(String).map((tip) => (
                            <label className="switch-item" key={tip} data-cy="tracking-tip">
                                <input
                                    type="radio"
                                    name="tip"
                                    value={tip}
                                    checked={selectedAmount === tip}
                                    onChange={handleChangeTip}
                                />
                                <div>
                                    {tip} <FormattedMessage id={currency.toLowerCase()} />
                                </div>
                            </label>
                        ))}
                        <label className="switch-item" data-cy="tracking-tip-custom">
                            <input
                                type="radio"
                                name="tip"
                                checked={customSelected || (!!customAmount && !selectedAmount)}
                                onChange={handleSelectCustom}
                            />
                            <div>
                                {customAmount && !selectedAmount ? (
                                    <>
                                        {customAmount}{" "}
                                        <FormattedMessage id={currency.toLowerCase()} />
                                    </>
                                ) : (
                                    <FormattedMessage id="custom" />
                                )}
                            </div>
                        </label>
                    </div>
                    {customSelected && (
                        <div
                            className={`custom-amount-field${isCustomInvalid ? " custom-amount-field-error" : ""}`}
                        >
                            <input
                                ref={customInputRef}
                                data-cy="custom-tip-input"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={customAmount}
                                onChange={handleCustomAmountChange}
                                onBlur={handleBlur}
                            />
                            <span>
                                <FormattedMessage id={currency.toLowerCase()} />
                            </span>
                        </div>
                    )}
                    {customSelected && isCustomInvalid && (
                        <div className="tip-hint">
                            ℹ️{" "}
                            <FormattedMessage
                                id="tip_range_hint"
                                values={{
                                    MIN: formatNumber(1),
                                    MAX: formatNumber(MAX_PRICE),
                                    CURRENCY: <FormattedMessage id={currency.toLowerCase()} />,
                                }}
                            />
                        </div>
                    )}

                    {googlePayLoaded &&
                        applePayLoaded &&
                        (!customSelected || !isCustomInvalid) &&
                        paymentType(customSelected ? customAmount || "0" : selectedAmount || "0")}
                </div>
            ) : (
                <TipsReceipt
                    tips={tips}
                    shareKey={shareKey}
                    shareKeyType={shareKeyType}
                    setAddMore={setAddMore}
                />
            )}
        </>
    );
});
